import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  TextField
} from "@mui/material";
import React, { forwardRef, useState } from "react";

import { formatPhoneNumber, phoneIsValid } from "../../../../Utils/Helpers";
import { PhoneFieldMasked } from "../../../Helpers/PhoneNumberField";
import { SearchParams } from "./Search";

interface Props {
  loading: boolean;
  setSearchParams: (params?: SearchParams) => void;
}

const PhoneInput = forwardRef((props, ref) => (
  <TextField {...props} inputRef={ref} label="Phone" name="search-phone" type="search" fullWidth />
));
PhoneInput.displayName = "PhoneInput";

const SearchForm: React.FC<Props> = ({ loading, setSearchParams }: Props) => {
  const [form, setForm] = useState({ phoneNumber: "", firstName: "", lastName: "", email: "", anyFlag: false });

  const formattedPhone = formatPhoneNumber(form.phoneNumber);
  const isValidSearch = form.email || form.firstName || form.lastName || phoneIsValid(formattedPhone);
  const updateSearchParams = () =>
    setSearchParams({
      phoneNumber: formattedPhone || undefined,
      firstName: form.firstName || undefined,
      lastName: form.lastName || undefined,
      email: form.email || undefined,
      anyFlag: form.anyFlag
    });
  const clear = () => {
    setSearchParams(undefined);
    setForm(f => ({ ...f, phoneNumber: "", firstName: "", lastName: "", email: "" }));
  };

  return loading ? (
    <Grid container item justifyContent="center">
      <Box m={17}>
        <CircularProgress />
      </Box>
    </Grid>
  ) : (
    <Grid item>
      <Grid container item justifyContent="space-between" alignItems="center">
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">Search by:</FormLabel>
            <RadioGroup
              onChange={(_, value) => setForm(f => ({ ...f, anyFlag: value === "true" }))}
              defaultValue="false"
              row
            >
              <FormControlLabel control={<Radio color="primary" />} label="All" value="true" />
              <FormControlLabel control={<Radio color="primary" />} label="Any" value="false" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <Button onClick={clear} color="secondary">
                Clear
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={updateSearchParams} disabled={!isValidSearch}>
                Search
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid item lg={7} xs container direction="column" spacing={1}>
          <Grid item>
            <FormControl variant="outlined" margin="dense" size="small" fullWidth sx={{ m: 0 }}>
              <InputLabel htmlFor="formatted-text-mask-input">Phone</InputLabel>
              <OutlinedInput
                margin="dense"
                size="small"
                value={form.phoneNumber}
                label="Phone"
                onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value.replace(/[^0-9]/g, "") }))}
                inputComponent={PhoneFieldMasked as never}
                onKeyPress={(e: { key: string }) => e.key === "Enter" && isValidSearch && updateSearchParams()}
                onPaste={e =>
                  setForm(f => ({ ...f, phoneNumber: e.clipboardData.getData("text").replace(/[^0-9]/g, "") }))
                }
              />
            </FormControl>
          </Grid>
          <Grid item>
            <Grid container spacing={1}>
              <Grid item xs>
                <TextField
                  value={form.firstName}
                  label="First Name"
                  type="search"
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  onKeyPress={e => e.key === "Enter" && isValidSearch && updateSearchParams()}
                  fullWidth
                />
              </Grid>
              <Grid item xs>
                <TextField
                  value={form.lastName}
                  label="Last Name"
                  type="search"
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  onKeyPress={e => e.key === "Enter" && isValidSearch && updateSearchParams()}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <TextField
              value={form.email}
              label="Email"
              type="search"
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              onKeyPress={e => e.key === "Enter" && isValidSearch && updateSearchParams()}
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchForm;
